import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { HTMLTags } from "../constants/HTMLTagsConstants";
import { ExportConfig } from "../constants/EditorConfigConstants";
import { LearnersReportDto, ReportDetailsDto } from "../_models/Reports";
import { AppConstants } from "../constants/constants";
import { ExportMessages } from "../constants/HttpResponseConstants";
import { DatePipe } from "@angular/common";
import { DateConstants } from "../constants/TimeConstants";
import { translation } from "../constants/DynamicTranslations";

@Injectable({
  providedIn: "root",
})
export class CustomExportService {
  constructor(private datePipe: DatePipe,
    @Inject(LOCALE_ID) protected localeId: string
  ) {}

  fullWidth: boolean = false;
  learnerReportExport(
    reportType: string,
    exportType: string = null,
    objects?: {
      column: string[];
      sourceData: LearnersReportDto[];

      //key/column are not part of current exported report
      excludeColumns?: string[];
      reportDetails?: ReportDetailsDto;
    }
  ): void {
    let content: string;
    let reportName = this.generateReportName(reportType);

    //Generate Content
    if (exportType === AppConstants.CSV_EXPORT)
      content = this.generateCSVContents(
        objects?.column,
        objects?.sourceData,
        objects?.excludeColumns
      );
    else
      content = this.generateTableContent(
        reportName,
        objects?.reportDetails,
        objects?.column,
        objects?.sourceData,
        objects?.excludeColumns
      );

    switch (exportType) {
      case AppConstants.HTML_EXPORT:
        this.initiateDownload(reportName, content, ExportConfig.TEXT_HTML);
        break;
      case AppConstants.CSV_EXPORT:
        this.initiateDownload(reportName, content, ExportConfig.TEXT_CSV);
        break;
      default:
        break;
    }
  }

  generateReportName(reportType: string): string {
    const today = new Date();

    let postReportName =
      AppConstants.UNDERSCORE_DELIM +
      today.getDate().toString().padStart(2, "0") +
      AppConstants.UNDERSCORE_DELIM +
      (today.getMonth() + 1).toString().padStart(2, "0") +
      AppConstants.UNDERSCORE_DELIM +
      today.getFullYear();

    switch (reportType) {
      case AppConstants.LEARNERS_RT:
        return AppConstants.LEARNERS + postReportName;
      case AppConstants.LEARNER_ACT_RT:
        return (
          AppConstants.LEARNERS +
          AppConstants.UNDERSCORE_DELIM +
          AppConstants.ACTIVITY +
          postReportName
        );
      case AppConstants.APP_NUMBER_RT:
        return AppConstants.APP_NUMBER_DISTANCE_TRAVEL + postReportName;
      case AppConstants.COMM_ENGLISH_RT:
        return AppConstants.COMM_ENGLISH_DISTANCE_TRAVEL + postReportName;
      case AppConstants.COMM_WELSH_RT:
        return AppConstants.COMM_WELSH_DISTANCE_TRAVEL + postReportName;
      case AppConstants.DIG_LIT_RT:
        return AppConstants.DIG_LIT_DISTANCE_TRAVEL + postReportName;
      case AppConstants.DIG_LIT_SELF_ASSESS_RT:
        return (
          AppConstants.DIG_LIT_SELF_ASSESS_DISTANCE_TRAVEL + postReportName
        );
      case AppConstants.OVERALL_INIT_ASSESS_RESULT_RT:
        return AppConstants.OVERALL_INIT_ASSESS_RESULT_NAME + postReportName;
      case AppConstants.APP_NUMBER_INIT_ASSESS_BREAKDOWN_RT:
        return (
          AppConstants.APP_NUMBER_INITIAL_ASSESSMENT_BREAKDOWN_NAME +
          postReportName
        );
      case AppConstants.COMM_ENGLISH_INIT_ASSESS_BREAKDOWN_RT:
        return (
          AppConstants.COMM_ENGLISH_INITIAL_ASSESSMENT_BREAKDOWN_NAME +
          postReportName
        );
      case AppConstants.COMM_WELSH_INIT_ASSESS_BREAKDOWN_RT:
        return (
          AppConstants.COMM_WELSH_INITIAL_ASSESSMENT_BREAKDOWN_NAME +
          postReportName
        );
      case AppConstants.DIG_LIT_INIT_ASSESS_BREAKDOWN_RT:
        return (
          AppConstants.DIG_LIT_INITIAL_ASSESSMENT_BREAKDOWN_NAME +
          postReportName
        );
      case AppConstants.APP_NUMBER_SKILL_RT:
        return AppConstants.APP_NUMBER_DISTANCE_TRAVEL_SKILL + postReportName;
      case AppConstants.COMM_ENGLISH_SKILL_RT:
        return AppConstants.COMM_ENGLISH_DISTANCE_TRAVEL_SKILL + postReportName;
      case AppConstants.COMM_WELSH_SKILL_RT:
        return AppConstants.COMM_WELSH_DISTANCE_TRAVEL_SKILL + postReportName;
      case AppConstants.DIG_LIT_SKILL_RT:
        return AppConstants.DIG_LIT_DISTANCE_TRAVEL_SKILL + postReportName;
      case AppConstants.DIG_LIT_SELF_ASSESS_SKILL_RT:
        return AppConstants.DIG_LIT_SELF_ASSESS_DISTANCE_TRAVEL_SKILL + postReportName;   
      case AppConstants.LEARNER_STATISTICS_ALL_COURSES_RT: 
        this.fullWidth = true;
        return AppConstants.LEARNER_STATISTICS_ALL_COURSES + postReportName;   
      case AppConstants.LEARNER_STATISTICS_SINGLE_COURSE_RT:
        this.fullWidth = true;
        return AppConstants.LEARNER_STATISTICS_SINGLE_COURSE + postReportName;      
      case AppConstants.SCREENER_STATISTICS_ALL_COURSES_RT:
        this.fullWidth = true;
        return AppConstants.SCREENER_STATISTICS_ALL_COURSES + postReportName; 
      case AppConstants.SCREENER_STATISTICS_SINGLE_COURSE_RT:
        this.fullWidth = true;
        return AppConstants.SCREENER_STATISTICS_SINGLE_COURSE + postReportName; 
      case AppConstants.INITIAL_ASSESSMENT_STATISTICS_ALL_COURSES_RT:
        this.fullWidth = true;
        return AppConstants.INITIAL_ASSESSMENT_STATISTICS_ALL_COURSES + postReportName; 
      case AppConstants.INITIAL_ASSESSMENT_STATISTICS_SINGLE_COURSE_RT:
        this.fullWidth = true;
        return AppConstants.INITIAL_ASSESSMENT_STATISTICS_SINGLE_COURSE + postReportName;
      case AppConstants.OVERALL_SCREENER_RESULT_RT:
        this.fullWidth = true;
        return AppConstants.OVERALL_SCREENER_RESULT + postReportName;
      default:
        AppConstants.BLANK;
        break;
    }
  }

  // Create a Blob and initiate download
  initiateDownload(
    reportName: string,
    content: string,
    blobType: string
  ): void {
    // Create a Blob and initiate download
    const blob = new Blob([content], { type: blobType });

    const link = document.createElement(HTMLTags.ANCHOR);
    link.download = reportName;
    link.href = URL.createObjectURL(blob);
    link.click();

    URL.revokeObjectURL(link.href);
  }

  htmlTableBanner(): string {
    return `
      <header>
            <img class="logo" src="${window.location.origin}/assets/image/logo.png" title="WEST" alt="Wales Essential Skills Toolkit">
      </header>`;
  }

  internalCSS(): string {
    return `
      <style>
        .logo {
          height: auto;
          width: auto;
          max-height: 64px;
          max-width: 300px;
        }
        header {
          background-color: #0077c4;
          margin-bottom: 20px;
          display: flex;
        }
        p {
          margin-bottom: 10px;
        }
        body {
          display: inline-block;
          ${this.fullWidth ? 'width: 99%;' : ''}
        }
        table {
          ${this.fullWidth ? 'width:100%;' : ''}
        }
        th, td {
          min-width: 150px;
        }
      </style>`;
  }

  generateTableContent(
    reportName: string,
    reportDetails: ReportDetailsDto,
    column: string[],
    sourceData: LearnersReportDto[],
    excludeColumns?: string[]
  ): string {
    return `${HTMLTags.HTML}
      ${HTMLTags.HEAD}
        ${HTMLTags.TITLE}${reportName}${HTMLTags.TITLE_CLS}
        ${this.internalCSS()}
      ${HTMLTags.HEAD_CLS}
      ${HTMLTags.BODY}
        ${this.htmlTableBanner()}
        ${HTMLTags.P(this.getTranslation(reportDetails?.name))}
        ${HTMLTags.P(this.getTranslation(reportDetails?.description))}
        ${HTMLTags.BR}
        ${HTMLTags.TABLE(1)}
          ${HTMLTags.THEAD}
            ${HTMLTags.TR}
              ${column
                .map((col) => HTMLTags.TH(this.getTranslation(col.toUpperCase())))
                .join(AppConstants.BLANK)}
            ${HTMLTags.TR_CLS}
          ${HTMLTags.THEAD_CLS}
          ${HTMLTags.TBODY}
            ${sourceData
              .map(
                (row: any) =>
                  `
              ${HTMLTags.TR}` +
                  Object.entries(row)
                    .filter(([key]) => !excludeColumns?.includes(key))
                    .map(([key, value]) => {
                      return `${HTMLTags.TD(this.dateTransform(key, value))}`;
                    })
                    .join(AppConstants.BLANK) +
                  `${HTMLTags.TR_CLS}`
              )
              .join(AppConstants.BLANK)}
          ${HTMLTags.TBODY_CLS}
        ${HTMLTags.TABLE_CLS}
      ${HTMLTags.BODY_CLS}
    ${HTMLTags.HTML_CLS}`;
  }

  generateCSVContents(
    column: string[],
    sourceData: LearnersReportDto[],
    excludeColumns?: string[]
  ): string {
    // Generate CSV header
    const headers = column
      .map((e) => `"${this.getTranslation(e.toUpperCase())}"`)
      .join(AppConstants.COMMA);

    // Generate CSV rows
    const rows = sourceData.map((row) =>
      // Get entries as [key, value] pairs
      Object.entries(row)
        // Exclude specific columns
        .filter(([key]) => !excludeColumns?.includes(key))
        .map(([key, value]) => `"${this.dateTransform(key, value)}"`)
        .join(AppConstants.COMMA)
    );

    // Combine header and rows
    return [headers, ...rows].join(HTMLTags.NEXT_LINE);
  }

  dateTransform(key: string, value: any): string {
    if (value === null || value === undefined || value === AppConstants.BLANK)
      return AppConstants.BLANK;

    if (
      key.includes(AppConstants.DATE_CREATED) ||
      key.includes(AppConstants.LAST_LOGIN_DATE) ||
      key.includes(AppConstants.OVERALL_ACTIVITY_DATE) ||
      key.includes(AppConstants.ACTIVITY_DATE)
    )
      return this.datePipe.transform(value, DateConstants.REPORT_DATE_FORMAT);

    if (key.includes(AppConstants.DATE_COMPLETED))
      return this.datePipe.transform(
        value,
        DateConstants.REPORT_DATE_COMPLETED_FORMAT
      );

    return this.getTranslation(value);
  }

  getTranslation(message: string): string {
    return this.localeId == AppConstants.WELSH_LANG_CODE
      ? translation[message] ?? message
      : message;
  }
}
